import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { ErpFieldValue, PendingDocument, DocumentFieldsResponse, DocumentFlagInfo, InvoiceAttachment, TenantErpFieldConfig, TenantErpFieldConfigItem } from '@schemas/api';
import { Button } from '@components/common/Button';
import { ConfidenceScore } from '@components/common/ConfidenceScore';
import { DocumentViewer } from '@components/common/DocumentViewer';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { useApproveExtraction, useRejectExtraction, useDeleteDocument, useExtractionStatus, useMissingReferences, useSubmitCorrections, useApplyDocumentFlagAction, useDeleteInvoiceAttachment, useInvoiceAttachments, useUploadInvoiceAttachments, useUpdateDocumentHeader } from '@api/hooks/usePendingDocuments';
import { useAPPostingWorkflowInfo, useAPPostingsForInvoice } from '@api/hooks/useAPPostings';
import { useApprovalRequestDetails, useApprovalStepResponses } from '@api/hooks/useApprovalRequests';
import { useOnboardingConfig } from '@api/hooks/useOnboarding';
import { useGetErpConnections } from '@api/hooks/useErpConnection';
import {
  useErpFieldCatalog,
  useErpFieldConfig,
  useUpdateErpFieldConfig,
} from '@api/hooks/useErpFields';
import {
  useCreatePaymentOrder,
  usePaymentOrders,
  useStartVendorPaymentProfile,
  useStripeConnection,
  useSubmitPaymentOrder,
  useVendorPaymentProfiles,
} from '@api/hooks/usePayments';
import { useAuthStore } from '@store/authStore';
import { getDocumentPermissionState } from '@/lib/documentPermissions';
import { APPostingApprovalModal } from '@components/APPosting/APPostingApprovalModal';
import { APPostingErrorAlert } from '@components/APPosting/APPostingErrorAlert';
import { MissingReferencesAlert } from './MissingReferencesAlert';
import { MissingReferencesPanel } from './MissingReferencesPanel';
import { DocumentFlagsPanel } from './DocumentFlagsPanel';
import { EditableExtractedFields } from './EditableExtractedFields';
import { EditableDocumentFields } from './EditableDocumentFields';
import { ApprovalChainReviewUI } from '@components/Approvals/ApprovalChainReviewUI';
import { DynamicErpFieldsEditor } from '@components/ERP/DynamicErpFieldsEditor';
import { LineItemsEditor } from './LineItemsEditor';
import { MatchStatusBadge } from './MatchStatusBadge';
import { MatchingPanel } from './MatchingPanel';
import { normalizeTaxIdEntries } from '@/lib/vendorTaxIds';
import {
  erpFieldKeyTail,
  getAvailableReviewErpFields,
  isReviewErpField,
  isReviewErpFieldKey,
} from '@/lib/reviewErpFields';
import { AlertTriangle, FileText, Lock, Paperclip, Plus, Trash2, Upload, X } from 'lucide-react';
import { detectLineMismatches } from '@/lib/lineItemValidation';
import type { MismatchCheckInput } from '@/lib/lineItemValidation';
import { FIELDS_BY_DOC_TYPE, type DocumentFieldOption } from '@/types/onboarding';

interface DocumentReviewModalProps {
  isOpen: boolean;
  document: PendingDocument;
  onClose: () => void;
  initialStage?: ReviewStage;
}

type ReviewStage = 'extraction' | 'approval_progress' | 'ap_posting' | 'payment';
type ReviewDocumentType = 'purchase_order' | 'receipt' | 'invoice';

const REVIEW_DOCUMENT_TYPE_OPTIONS = [
  { id: 'invoice', label: 'Invoice' },
  { id: 'purchase_order', label: 'Purchase Order' },
  { id: 'receipt', label: 'Receipt' },
];

const formatExtractionFieldLabel = (fieldKey: string) =>
  fieldKey
    .replace(/^line_items\./, 'Line Item ')
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const isBillPushableReviewConfig = (config: TenantErpFieldConfig) => isReviewErpField(config.field);

const formatAttachmentSize = (size?: number | null) => {
  if (!size) {
    return '';
  }
  if (size < 1024 * 1024) {
    return `${Math.max(1, Math.round(size / 1024))} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

interface InvoiceAttachmentsPanelProps {
  attachments: InvoiceAttachment[];
  isLoading: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  selectedBlobUri: string | null;
  onSelectBlobUri: (blobUri: string | null) => void;
  onUpload: (files: File[]) => void;
  onDelete: (attachmentId: string) => void;
}

const InvoiceAttachmentsPanel: React.FC<InvoiceAttachmentsPanelProps> = ({
  attachments,
  isLoading,
  isUploading,
  isDeleting,
  selectedBlobUri,
  onSelectBlobUri,
  onUpload,
  onDelete,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const selectedAttachment = attachments.find((attachment) => attachment.blob_uri === selectedBlobUri) || null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      onUpload(files);
    }
    event.target.value = '';
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Invoice Attachments
          </h3>
          {attachments.length > 0 && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {attachments.length}
            </span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.tiff,.gif"
          className="hidden"
          onChange={handleFileChange}
        />
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-2"
        >
          <Upload className="h-4 w-4" />
          {isUploading ? 'Uploading...' : 'Attach'}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading attachments...</p>
      ) : attachments.length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No additional documents attached.
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)]">
          <div className="space-y-2">
            {attachments.map((attachment) => {
              const isSelected = attachment.blob_uri === selectedBlobUri;
              const sizeLabel = formatAttachmentSize(attachment.file_size);
              return (
                <div
                  key={attachment.id}
                  className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${
                    isSelected
                      ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950'
                      : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onSelectBlobUri(isSelected ? null : attachment.blob_uri)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <FileText className="h-4 w-4 flex-shrink-0 text-gray-500" />
                      <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {attachment.original_filename}
                      </span>
                    </span>
                    <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                      {new Date(attachment.created_at).toLocaleDateString()}
                      {sizeLabel ? ` · ${sizeLabel}` : ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={() => onDelete(attachment.id)}
                    className="rounded p-1 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-950 dark:hover:text-red-300"
                    aria-label={`Remove ${attachment.original_filename}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="min-h-[260px]">
            {selectedAttachment ? (
              <DocumentViewer
                blobUri={selectedAttachment.blob_uri}
                className="h-[260px] lg:h-[320px]"
              />
            ) : (
              <div className="flex h-[260px] items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 lg:h-[320px]">
                Select an attachment to preview.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const DocumentReviewModal: React.FC<DocumentReviewModalProps> = ({
  isOpen,
  document,
  onClose,
  initialStage = 'extraction',
}) => {
  const [currentDocument, setCurrentDocument] = useState<PendingDocument>(document);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [correctionNotes, setCorrectionNotes] = useState('');
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [approvalRequestId, setApprovalRequestId] = useState<string | null>(null);
  const [activeStage, setActiveStage] = useState<ReviewStage>('extraction');
  const [showMismatchDialog, setShowMismatchDialog] = useState(false);
  const [mismatchIssues, setMismatchIssues] = useState<string[]>([]);
  const [showMatchingPanel, setShowMatchingPanel] = useState(false);
  const [headerMatchingRelevantChange, setHeaderMatchingRelevantChange] = useState(false);
  const [selectedAPPostingId, setSelectedAPPostingId] = useState<string | null>(null);
  const [selectedAttachmentBlobUri, setSelectedAttachmentBlobUri] = useState<string | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const pendingMismatchAction = useRef<{
    action: 'approve' | 'corrections';
    documentType: string;
    documentId: string;
    user: string;
    notes?: string;
    corrections?: Record<string, unknown>;
    dynamic_erp_fields?: ErpFieldValue[];
    erp_connection_id?: string;
  } | null>(null);
  const [lineItems, setLineItems] = useState<any[]>([]);
  const [manualLineItemFields, setManualLineItemFields] = useState<string[]>([]);
  const [selectedLineItemFieldToAdd, setSelectedLineItemFieldToAdd] = useState('');
  const [dynamicErpValues, setDynamicErpValues] = useState<ErpFieldValue[]>([]);
  const [selectedHeaderErpFieldId, setSelectedHeaderErpFieldId] = useState('');
  const [selectedLineErpFieldId, setSelectedLineErpFieldId] = useState('');
  const [selectedErpConnectionId, setSelectedErpConnectionId] = useState('');
  const [headerDraftType, setHeaderDraftType] = useState<ReviewDocumentType>(document.type);
  const [headerDraftNumber, setHeaderDraftNumber] = useState(document.external_number || document.po_number || '');
  const editableFieldsRef = useRef<any>(null);
  const docType = currentDocument.type as ReviewDocumentType;

  const approveExtraction = useApproveExtraction();
  const rejectExtraction = useRejectExtraction();
  const deleteDocument = useDeleteDocument();
  const submitCorrections = useSubmitCorrections();
  const updateDocumentHeader = useUpdateDocumentHeader();
  const applyDocumentFlagAction = useApplyDocumentFlagAction();
  const uploadInvoiceAttachments = useUploadInvoiceAttachments();
  const deleteInvoiceAttachment = useDeleteInvoiceAttachment();
  const extractionStatusQuery = useExtractionStatus(currentDocument.type, currentDocument.id);
  const invoiceAttachmentsQuery = useInvoiceAttachments(
    currentDocument.type === 'invoice' ? currentDocument.id : undefined,
    isOpen && currentDocument.type === 'invoice',
  );
  const missingReferencesQuery = useMissingReferences(currentDocument.type, currentDocument.id);
  const relatedAPPostingsQuery = useAPPostingsForInvoice(
    currentDocument.type === 'invoice' ? currentDocument.id : null,
    isOpen && currentDocument.type === 'invoice',
  );
  const approvalRequestQuery = useApprovalRequestDetails(approvalRequestId);
  const approvalHistoryQuery = useApprovalStepResponses(approvalRequestId);
  const paymentOrdersQuery = usePaymentOrders('ap_posting');
  const paymentOrders = paymentOrdersQuery.data || [];
  const { data: stripeConnection } = useStripeConnection();
  const vendorProfilesQuery = useVendorPaymentProfiles();
  const vendorProfiles = vendorProfilesQuery.data || [];
  const createPaymentOrder = useCreatePaymentOrder();
  const submitPaymentOrder = useSubmitPaymentOrder();
  const startVendorPaymentProfile = useStartVendorPaymentProfile();
  const { data: onboardingConfig } = useOnboardingConfig();
  const { data: erpConnections = [] } = useGetErpConnections(isOpen);
  const activeErpConnections = useMemo(
    () => erpConnections.filter((connection) => connection.status === 'active'),
    [erpConnections],
  );
  const selectedErpConnection = activeErpConnections.find(
    (connection) => connection.id === selectedErpConnectionId,
  );
  const persistedErpConnectionId = currentDocument.erp_connection_id || '';
  const hasActivePersistedErpConnection = activeErpConnections.some(
    (connection) => connection.id === persistedErpConnectionId,
  );
  const showErpConnectionControl = activeErpConnections.length > 1 || (
    activeErpConnections.length > 0 && !hasActivePersistedErpConnection
  );
  const erpConnectionHasChanges = Boolean(selectedErpConnectionId) && (
    selectedErpConnectionId !== persistedErpConnectionId
  );
  const hasSelectedErpConnection = Boolean(selectedErpConnectionId);
  const documentErpCatalogQuery = useErpFieldCatalog('document_review', isOpen && hasSelectedErpConnection, selectedErpConnectionId || undefined);
  const documentErpConfigQuery = useErpFieldConfig('document_review', isOpen && hasSelectedErpConnection, selectedErpConnectionId || undefined);
  const updateErpFieldConfig = useUpdateErpFieldConfig(selectedErpConnectionId || undefined);
  const currentUser = useAuthStore((state) => state.currentUser);
  const hasPermission = useAuthStore((state) => state.hasPermission);

  useEffect(() => {
    setCurrentDocument(document);
    setHeaderDraftType(document.type);
    setHeaderDraftNumber(document.external_number || document.po_number || '');
  }, [document]);

  const selectedExtractionFields = onboardingConfig?.extraction_fields[docType];
  const adminExtractionFieldCatalog = useMemo<DocumentFieldOption[]>(() => {
    const staticFieldMetadata = new Map(
      FIELDS_BY_DOC_TYPE[docType].map((field) => [field.key, field])
    );

    return (selectedExtractionFields || []).map((fieldKey) => {
      const metadata = staticFieldMetadata.get(fieldKey);
      return {
        key: fieldKey,
        label: metadata?.label ?? formatExtractionFieldLabel(fieldKey),
        description: metadata?.description,
        isDefault: metadata?.isDefault ?? false,
      };
    });
  }, [docType, selectedExtractionFields]);

  const selectedHeaderFields = selectedExtractionFields?.filter(
    (k) => !k.startsWith('line_items.')
  );

  const normalizeLineItemFieldKey = useCallback((key: string) => {
    const fieldKey = key.replace('line_items.', '');
    if (fieldKey === 'qty' || fieldKey === 'quantity_ordered') {
      return 'quantity';
    }
    return fieldKey;
  }, []);

  const selectedLineItemFields = useMemo(() => {
    if (!selectedExtractionFields && manualLineItemFields.length === 0) {
      return undefined;
    }

    const configuredFields = (selectedExtractionFields || [])
      .filter((k) => k.startsWith('line_items.'))
      .map(normalizeLineItemFieldKey);

    return Array.from(new Set([...configuredFields, ...manualLineItemFields]));
  }, [manualLineItemFields, normalizeLineItemFieldKey, selectedExtractionFields]);

  const addableLineItemFieldOptions = useMemo(() => {
    if (!selectedExtractionFields) {
      return [];
    }

    const visibleFields = new Set(selectedLineItemFields || []);
    const seen = new Set<string>();

    return adminExtractionFieldCatalog
      .filter((option) => option.key.startsWith('line_items.'))
      .map((option) => ({
        ...option,
        fieldKey: normalizeLineItemFieldKey(option.key),
      }))
      .filter((option) => {
        if (seen.has(option.fieldKey) || visibleFields.has(option.fieldKey)) {
          return false;
        }
        seen.add(option.fieldKey);
        return true;
      });
  }, [adminExtractionFieldCatalog, normalizeLineItemFieldKey, selectedExtractionFields, selectedLineItemFields]);

  const showLineItemsSection = selectedExtractionFields
    ? ((selectedLineItemFields?.length ?? 0) > 0 || addableLineItemFieldOptions.length > 0)
    : true;

  const isLoading =
    approveExtraction.isPending ||
    rejectExtraction.isPending ||
    deleteDocument.isPending ||
    submitCorrections.isPending;

  const originalLineItems = extractionStatusQuery.data?.document?.lines || [];
  const lineMatchingRelevantChange = useMemo(
    () => showLineItemsSection && JSON.stringify(lineItems || []) !== JSON.stringify(originalLineItems || []),
    [lineItems, originalLineItems, showLineItemsSection]
  );
  const matchingWillRecalculate = headerMatchingRelevantChange || lineMatchingRelevantChange;

  const normalizeTaxIds = useCallback((taxIds?: DocumentFieldsResponse['tax_ids']) => {
    const entries = normalizeTaxIdEntries(taxIds)
      .sort((left, right) => left.type.localeCompare(right.type));
    return entries.length > 0 ? entries : null;
  }, []);

  const normalizeTaxItems = useCallback((taxItems?: DocumentFieldsResponse['tax_items']) => {
    if (!Array.isArray(taxItems)) {
      return null;
    }

    const normalizedItems = taxItems
      .filter((item): item is NonNullable<DocumentFieldsResponse['tax_items']>[number] => Boolean(item && typeof item === 'object'))
      .map((item) => {
        const normalizedItem: Record<string, unknown> = {};

        Object.entries(item).forEach(([key, value]) => {
          if (typeof value === 'string') {
            const trimmedValue = value.trim();
            if (trimmedValue) {
              normalizedItem[key] = trimmedValue;
            }
            return;
          }

          if (value !== null && value !== undefined) {
            normalizedItem[key] = value;
          }
        });

        return normalizedItem;
      })
      .filter((item) => Object.keys(item).length > 0);

    return normalizedItems.length > 0 ? normalizedItems : null;
  }, []);

  // Initialize line items from extraction data
  useEffect(() => {
    if (extractionStatusQuery.data?.document?.lines) {
      setLineItems(extractionStatusQuery.data.document.lines);
    } else if (currentDocument.extraction_data) {
      const lines = currentDocument.extraction_data.lines || currentDocument.extraction_data.line_items;
      if (Array.isArray(lines) && lines.length > 0 && lines[0]?.value) {
        setLineItems(Array.isArray(lines[0].value) ? lines[0].value : lines);
      } else if (Array.isArray(lines)) {
        setLineItems(lines);
      } else {
        setLineItems([]);
      }
    } else {
      setLineItems([]);
    }
  }, [currentDocument.extraction_data, extractionStatusQuery.data?.document?.lines]);

  useEffect(() => {
    const activeApprovalRequestId = extractionStatusQuery.data?.approval_request_id;
    if (activeApprovalRequestId) {
      setApprovalRequestId(activeApprovalRequestId);
    } else if (!activeApprovalRequestId) {
      setApprovalRequestId(null);
      setActiveStage((stage) => (stage === 'approval_progress' ? 'extraction' : stage));
    }
  }, [extractionStatusQuery.data?.approval_request_id]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setActiveStage(initialStage);
    setShowMatchingPanel(false);
    setHeaderMatchingRelevantChange(false);
    setSelectedAttachmentBlobUri(null);
    setManualLineItemFields([]);
    setSelectedLineItemFieldToAdd('');
  }, [currentDocument.id, initialStage, isOpen]);

  useEffect(() => {
    setDynamicErpValues(extractionStatusQuery.data?.dynamic_erp_fields?.values || []);
  }, [currentDocument.id, extractionStatusQuery.data?.dynamic_erp_fields?.values]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const responseConnectionId = extractionStatusQuery.data?.dynamic_erp_fields?.erp_connection_id;
    const availableIds = new Set(activeErpConnections.map((connection) => connection.id));
    const nextConnectionId =
      (persistedErpConnectionId && availableIds.has(persistedErpConnectionId) ? persistedErpConnectionId : undefined)
      || (responseConnectionId && availableIds.has(responseConnectionId) ? responseConnectionId : undefined)
      || (activeErpConnections.length === 1 ? activeErpConnections[0].id : undefined)
      || '';
    setSelectedErpConnectionId((current) => current && availableIds.has(current) ? current : nextConnectionId);
  }, [activeErpConnections, isOpen, extractionStatusQuery.data?.dynamic_erp_fields?.erp_connection_id, persistedErpConnectionId]);

  useEffect(() => {
    if (!selectedAttachmentBlobUri) {
      return;
    }
    const stillExists = (invoiceAttachmentsQuery.data || []).some(
      (attachment) => attachment.blob_uri === selectedAttachmentBlobUri
    );
    if (!stillExists) {
      setSelectedAttachmentBlobUri(null);
    }
  }, [invoiceAttachmentsQuery.data, selectedAttachmentBlobUri]);

  const relatedAPPostings = useMemo(
    () => relatedAPPostingsQuery.data || [],
    [relatedAPPostingsQuery.data],
  );
  const selectedAPPosting = useMemo(() => {
    if (relatedAPPostings.length === 0) {
      return null;
    }
    return relatedAPPostings.find((posting) => posting.id === selectedAPPostingId) || relatedAPPostings[0];
  }, [relatedAPPostings, selectedAPPostingId]);
  const apPostingWorkflowInfoQuery = useAPPostingWorkflowInfo(
    selectedAPPosting?.id || '',
    isOpen && Boolean(selectedAPPosting),
  );
  const apPostingApprovalRequestId = apPostingWorkflowInfoQuery.data?.approval_request_id ?? null;
  const apPostingApprovalStatus =
    apPostingWorkflowInfoQuery.data?.approval_status ?? selectedAPPosting?.approval_status ?? null;
  const apPostingError =
    apPostingWorkflowInfoQuery.data?.posting_error ?? selectedAPPosting?.posting_error ?? null;
  const apPostingDisplayStatus =
    selectedAPPosting?.status === 'posting_failed'
      ? selectedAPPosting.status
      : apPostingApprovalStatus ?? selectedAPPosting?.status ?? 'waiting';
  const paymentOrdersByPostingId = useMemo(
    () =>
      paymentOrders.reduce<Record<string, (typeof paymentOrders)[number]>>((acc, order) => {
        if (order.ap_posting_id) {
          acc[order.ap_posting_id] = order;
        }
        return acc;
      }, {}),
    [paymentOrders],
  );
  const vendorProfilesByVendorId = useMemo(
    () =>
      vendorProfiles.reduce<Record<string, (typeof vendorProfiles)[number]>>((acc, profile) => {
        acc[profile.vendor_id] = profile;
        return acc;
      }, {}),
    [vendorProfiles],
  );
  const selectedPaymentOrder = selectedAPPosting
    ? paymentOrdersByPostingId[selectedAPPosting.id]
    : undefined;
  const selectedVendorProfile =
    selectedAPPosting?.vendor_id ? vendorProfilesByVendorId[selectedAPPosting.vendor_id] : undefined;

  useEffect(() => {
    if (!isOpen || currentDocument.type !== 'invoice' || relatedAPPostings.length === 0) {
      setSelectedAPPostingId(null);
      return;
    }

    setSelectedAPPostingId((currentId) => {
      if (currentId && relatedAPPostings.some((posting) => posting.id === currentId)) {
        return currentId;
      }
      const preferredPosting =
        relatedAPPostings.find((posting) => posting.status === 'pending_approval') ||
        relatedAPPostings.find((posting) => posting.status === 'approved') ||
        relatedAPPostings.find((posting) => posting.status !== 'discarded') ||
        relatedAPPostings[0];
      return preferredPosting.id;
    });
  }, [currentDocument.type, isOpen, relatedAPPostings]);

  const handleModalLineItemsChange = useCallback((newItems: any[]) => {
    setLineItems(newItems);
  }, []);

  const handleAddLineItemField = useCallback(() => {
    if (!selectedLineItemFieldToAdd) {
      return;
    }

    setManualLineItemFields((prev) => (
      prev.includes(selectedLineItemFieldToAdd)
        ? prev
        : [...prev, selectedLineItemFieldToAdd]
    ));
    setSelectedLineItemFieldToAdd('');
  }, [selectedLineItemFieldToAdd]);

  // Get field-level confidence from the extraction status response
  const perFieldConf = extractionStatusQuery.data?.per_field_conf || {};

  // Use fresh missing references from dedicated query
  const missingReferences = missingReferencesQuery.data || [];
  const hasBlockingIssues = missingReferences.some(ref => ref.is_critical && ref.status === 'unresolved');

  // Fallback: Helper function to convert extracted fields to corrections format (legacy)
  const extractCorrections = useCallback((extractionData: Record<string, any>): Record<string, any> => {
    const corrections: Record<string, any> = {};
    const original = currentDocument.extraction_data || {};

    // Compare each field and track changes
    Object.entries(extractionData).forEach(([key, value]) => {
      if (JSON.stringify(original[key]) !== JSON.stringify(value)) {
        // Map extraction field names to model field names
        const fieldMapping: Record<string, string> = {
          po_number: 'po_number',
          external_receipt_number: 'external_receipt_number',
          external_invoice_number: 'external_invoice_number',
          currency: 'currency',
          subtotal: 'subtotal',
          discount: 'discount_total',
          additional_charges: 'additional_charges',
          total_amount: 'total',
          tax: 'tax_total',
          tax_total: 'tax_total',
          vendor_name: 'vendor_name_extracted',
          receipt_date: 'document_date',
          document_date: 'document_date',
          invoice_date: 'invoice_date',
          due_date: 'due_date',
          payment_terms: 'payment_terms',
          tax_items: 'tax_items',
          line_items: 'lines',
        };

        const mappedKey = fieldMapping[key] || key;

        // Handle line items specially
        if (key === 'line_items' && Array.isArray(value)) {
          const lineItemCorrections = value.map((item, idx) => ({
            line_number: idx + 1,
            ...item,
          }));
          corrections.lines = lineItemCorrections;
        } else if (key === 'tax_items') {
          corrections.tax_items = normalizeTaxItems(
            Array.isArray(value) && value.length > 0 && Array.isArray(value[0]?.value)
              ? value[0].value
              : value
          );
        } else if (key !== 'vendor_name') {
          // For scalar values, just take the chosen value if it's a candidate structure
          if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'value' in value[0]) {
            corrections[mappedKey] = value[0].value;
          } else {
            corrections[mappedKey] = value;
          }
        }
      }
    });

    return corrections;
  }, [currentDocument.extraction_data]);

  // Helper function to convert top-level fields to corrections format
  const extractCorrectionsFromTopLevel = useCallback((documentData: DocumentFieldsResponse): Record<string, any> => {
    const corrections: Record<string, any> = {};
    const original = (extractionStatusQuery.data?.document || {}) as DocumentFieldsResponse;

    // Only include fields that have changed
    if (documentData.external_invoice_number !== original.external_invoice_number) {
      corrections.external_invoice_number = documentData.external_invoice_number;
    }
    if (documentData.external_receipt_number !== original.external_receipt_number) {
      corrections.external_receipt_number = documentData.external_receipt_number;
    }
    // Vendor fields: send vendor_id if changed (vendor_name is derived from vendor lookup)
    if (documentData.vendor_id !== original.vendor_id) {
      corrections.vendor_id = documentData.vendor_id;
    }
    // Also include vendor_name_extracted if it changed (for fallback display when vendor_id is null)
    if (documentData.vendor_name_extracted !== original.vendor_name_extracted) {
      corrections.vendor_name_extracted = documentData.vendor_name_extracted;
    }
    if (documentData.po_number !== original.po_number) {
      corrections.po_number = documentData.po_number;
    }
    if (documentData.subtotal !== original.subtotal) {
      corrections.subtotal = documentData.subtotal;
    }
    if (documentData.total !== original.total) {
      corrections.total = documentData.total;
    }
    if (documentData.discount_total !== original.discount_total) {
      corrections.discount_total = documentData.discount_total;
    }
    if (documentData.additional_charges !== original.additional_charges) {
      corrections.additional_charges = documentData.additional_charges;
    }
    if (documentData.tax_total !== original.tax_total) {
      corrections.tax_total = documentData.tax_total;
    }
    if (
      JSON.stringify(normalizeTaxItems(documentData.tax_items)) !==
      JSON.stringify(normalizeTaxItems(original.tax_items))
    ) {
      corrections.tax_items = normalizeTaxItems(documentData.tax_items);
    }
    if (
      JSON.stringify(normalizeTaxIds(documentData.tax_ids)) !==
      JSON.stringify(normalizeTaxIds(original.tax_ids))
    ) {
      corrections.tax_ids = normalizeTaxIds(documentData.tax_ids);
    }
    if (documentData.currency !== original.currency) {
      corrections.currency = documentData.currency;
    }
    if (documentData.invoice_date !== original.invoice_date) {
      corrections.invoice_date = documentData.invoice_date;
    }
    if (documentData.due_date !== original.due_date) {
      corrections.due_date = documentData.due_date;
    }
    if (documentData.payment_terms !== original.payment_terms) {
      corrections.payment_terms = documentData.payment_terms;
    }
    if (documentData.receipt_date !== original.receipt_date) {
      corrections.receipt_date = documentData.receipt_date;
    }
    if (documentData.document_date !== original.document_date) {
      corrections.document_date = documentData.document_date;
    }
    if (showLineItemsSection && JSON.stringify(documentData.lines) !== JSON.stringify(original.lines)) {
      corrections.lines = documentData.lines;
    }
    return corrections;
  }, [extractionStatusQuery.data?.document, normalizeTaxIds, normalizeTaxItems, showLineItemsSection]);

  // Get current document data (use passed document, since missing references are queried separately)
  const currentDocumentFlags =
    extractionStatusQuery.data?.document_flags ?? currentDocument.document_flags ?? [];
  const currentMatchDocument = {
    ...currentDocument,
    match_status: extractionStatusQuery.data?.document?.match_status ?? currentDocument.match_status,
    match_summary: extractionStatusQuery.data?.document?.match_summary ?? currentDocument.match_summary,
  };

  const handleReferencesResolved = useCallback(async () => {
    // Refetch both missing references and extraction data so linked vendor fields render immediately.
    await Promise.all([missingReferencesQuery.refetch(), extractionStatusQuery.refetch()]);

    // Trigger a component update
    setRefreshKey((prev) => prev + 1);
  }, [extractionStatusQuery, missingReferencesQuery]);

  const handleApprovalChainComplete = useCallback(async () => {
    await Promise.all([
      extractionStatusQuery.refetch(),
      approvalRequestQuery.refetch(),
      relatedAPPostingsQuery.refetch(),
    ]);
    setActiveStage('approval_progress');
  }, [approvalRequestQuery, extractionStatusQuery, relatedAPPostingsQuery]);

  const handleApprovalChainRejected = useCallback(async () => {
    await Promise.all([
      extractionStatusQuery.refetch(),
      approvalRequestQuery.refetch(),
      relatedAPPostingsQuery.refetch(),
    ]);
    setActiveStage('approval_progress');
  }, [approvalRequestQuery, extractionStatusQuery, relatedAPPostingsQuery]);

  const handleAPPostingApprovalChainComplete = useCallback(async () => {
    await Promise.all([
      relatedAPPostingsQuery.refetch(),
      apPostingWorkflowInfoQuery.refetch(),
    ]);
    setActiveStage('payment');
  }, [apPostingWorkflowInfoQuery, relatedAPPostingsQuery]);

  const handleAPPostingApprovalChainRejected = useCallback(async () => {
    await Promise.all([
      relatedAPPostingsQuery.refetch(),
      apPostingWorkflowInfoQuery.refetch(),
    ]);
    setActiveStage('approval_progress');
  }, [apPostingWorkflowInfoQuery, relatedAPPostingsQuery]);

  const handleAPPostingReviewComplete = useCallback(async () => {
    await Promise.all([
      relatedAPPostingsQuery.refetch(),
      apPostingWorkflowInfoQuery.refetch(),
      paymentOrdersQuery.refetch(),
    ]);
    setActiveStage('payment');
  }, [apPostingWorkflowInfoQuery, paymentOrdersQuery, relatedAPPostingsQuery]);

  const handleCreatePayment = useCallback(
    async (releaseMode: 'manual' | 'scheduled') => {
      if (!selectedAPPosting) {
        return;
      }

      const order = await createPaymentOrder.mutateAsync({
        source_type: 'ap_posting',
        source_id: selectedAPPosting.id,
        amount: selectedAPPosting.posting_amount.toFixed(2),
        currency: selectedAPPosting.currency,
        release_mode: releaseMode,
        scheduled_for: releaseMode === 'scheduled' && scheduleDate
          ? new Date(`${scheduleDate}T12:00:00`).toISOString()
          : null,
        remittance_note: `AP posting ${selectedAPPosting.invoice_num}`,
      });

      if (releaseMode === 'manual') {
        await submitPaymentOrder.mutateAsync(order.id);
      }

      setScheduleDate('');
      await paymentOrdersQuery.refetch();
    },
    [createPaymentOrder, paymentOrdersQuery, scheduleDate, selectedAPPosting, submitPaymentOrder],
  );

  const handleSubmitExistingPaymentOrder = useCallback(
    async (paymentOrderId: string) => {
      await submitPaymentOrder.mutateAsync(paymentOrderId);
      await paymentOrdersQuery.refetch();
    },
    [paymentOrdersQuery, submitPaymentOrder],
  );

  const handleStartVendorPaymentRequest = useCallback(async () => {
    if (!selectedAPPosting?.vendor_id || !currentDocument.id) {
      return;
    }

    await startVendorPaymentProfile.mutateAsync({
      vendorId: selectedAPPosting.vendor_id,
      payload: {
        return_path: `/reviews?document_type=invoice&document_id=${encodeURIComponent(currentDocument.id)}&open_review=1&review_stage=payment`,
        currency: selectedAPPosting.currency || undefined,
      },
    });
    await vendorProfilesQuery.refetch();
  }, [currentDocument.id, selectedAPPosting, startVendorPaymentProfile, vendorProfilesQuery]);

  const handleApprove = async () => {
    try {
      // Get edited data from the editable fields component
      const editedData = editableFieldsRef.current?.getEditedData() || {};
      if (showLineItemsSection) {
        editedData.lines = lineItems;
      }

      // Build corrections - use top-level fields if available, otherwise use extraction data
      let corrections: Record<string, any> = {};

      if (extractionStatusQuery.data?.document) {
        const documentData: DocumentFieldsResponse = {
          id: extractionStatusQuery.data.document.id,
          extraction_status: extractionStatusQuery.data.document.extraction_status,
          match_status: extractionStatusQuery.data.document.match_status,
          ...editedData,
        };
        corrections = extractCorrectionsFromTopLevel(documentData);
      } else {
        corrections = extractCorrections(editedData);
      }

      if (!currentDocument.id) throw new Error('Document ID is required');
      if (!currentUser?.id) throw new Error('Current user is required');

      // Run mismatch checks before submitting.
      const doc = extractionStatusQuery.data?.document;
      const checkInput: MismatchCheckInput = {
        lineItems: showLineItemsSection ? lineItems : [],
        subtotal: editedData.subtotal ?? doc?.subtotal,
        docTotal: editedData.total ?? doc?.total,
        taxTotal: editedData.tax_total ?? doc?.tax_total,
        discountTotal: editedData.discount_total ?? doc?.discount_total,
        additionalCharges: editedData.additional_charges ?? doc?.additional_charges,
      };
      const issues = detectLineMismatches(checkInput);
      if (issues.length > 0) {
        // Store the payload so mismatch confirmation can reuse it.
        pendingMismatchAction.current = {
          action: 'approve',
          documentType: currentDocument.type,
          documentId: currentDocument.id,
          user: currentUser.id,
          notes: 'Extraction approved during review',
          corrections: Object.keys(corrections).length > 0 ? corrections : undefined,
          dynamic_erp_fields: pushableDynamicErpValues.length > 0 ? pushableDynamicErpValues : undefined,
          erp_connection_id: selectedErpConnectionId || undefined,
        };
        setMismatchIssues(issues);
        setShowMismatchDialog(true);
        return;
      }

      const response = await approveExtraction.mutateAsync({
        documentType: currentDocument.type,
        documentId: currentDocument.id,
        user: currentUser.id,
        notes: 'Extraction approved during review',
        corrections: Object.keys(corrections).length > 0 ? corrections : undefined,
        dynamic_erp_fields: pushableDynamicErpValues.length > 0 ? pushableDynamicErpValues : undefined,
        erp_connection_id: selectedErpConnectionId || undefined,
      });

      if (response.approval_request_id) {
        setApprovalRequestId(response.approval_request_id);
        setActiveStage('approval_progress');
      } else {
        if (currentDocument.type === 'invoice') {
          await Promise.all([
            extractionStatusQuery.refetch(),
            relatedAPPostingsQuery.refetch(),
          ]);
          setActiveStage('ap_posting');
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error('Approval failed:', error);
    }
  };

  const handleConfirmMismatchAction = async () => {
    const payload = pendingMismatchAction.current;
    if (!payload) return;
    pendingMismatchAction.current = null;

    try {
      if (payload.action === 'approve') {
        const response = await approveExtraction.mutateAsync({
          documentType: payload.documentType,
          documentId: payload.documentId,
          user: payload.user,
          notes: payload.notes,
          corrections: payload.corrections,
          dynamic_erp_fields: payload.dynamic_erp_fields,
          erp_connection_id: payload.erp_connection_id,
        });

        setShowMismatchDialog(false);
        setMismatchIssues([]);
        if (response.approval_request_id) {
          setApprovalRequestId(response.approval_request_id);
          setActiveStage('approval_progress');
        } else {
          if (payload.documentType === 'invoice') {
            await Promise.all([
              extractionStatusQuery.refetch(),
              relatedAPPostingsQuery.refetch(),
            ]);
            setActiveStage('ap_posting');
          } else {
            onClose();
          }
        }
      } else {
        await submitCorrections.mutateAsync({
          documentType: payload.documentType as 'invoice' | 'purchase_order' | 'receipt',
          documentId: payload.documentId,
          user: payload.user,
          corrections: payload.corrections || {},
          notes: payload.notes,
          dynamic_erp_fields: payload.dynamic_erp_fields,
          erp_connection_id: payload.erp_connection_id,
        });
        setShowMismatchDialog(false);
        setMismatchIssues([]);
        await extractionStatusQuery.refetch();
        setShowCorrectionForm(false);
        setRefreshKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Mismatch-confirmed action failed:', error);
    }
  };

  const handleReject = async () => {
    try {
      if (!currentDocument.id) throw new Error('Document ID is required');
      if (!currentUser?.id) throw new Error('Current user is required');
      await rejectExtraction.mutateAsync({
        documentType: currentDocument.type,
        documentId: currentDocument.id,
        user: currentUser.id,
        reason: rejectionReason,
      });
      setCurrentDocument((prev) => ({
        ...prev,
        status: 'rejected',
        extraction_status: 'rejected',
        review_notes: rejectionReason,
      }));
      setShowRejectForm(false);
      setRejectionReason('');
      await extractionStatusQuery.refetch();
    } catch (error) {
      console.error('Rejection failed:', error);
    }
  };

  const handleDelete = async () => {
    try {
      if (!currentDocument.id) throw new Error('Document ID is required');
      if (!currentUser?.id) throw new Error('Current user is required');
      await deleteDocument.mutateAsync({
        documentType: currentDocument.type,
        documentId: currentDocument.id,
        user: currentUser.id,
        reason: 'Deleted from review modal',
      });
      onClose();
    } catch (error) {
      console.error('Deletion failed:', error);
    }
  };

  const handleUploadInvoiceAttachments = useCallback(
    async (files: File[]) => {
      if (currentDocument.type !== 'invoice' || !currentDocument.id || files.length === 0) {
        return;
      }
      const uploaded = await uploadInvoiceAttachments.mutateAsync({
        invoiceId: currentDocument.id,
        files,
      });
      await invoiceAttachmentsQuery.refetch();
      if (uploaded[0]?.blob_uri) {
        setSelectedAttachmentBlobUri(uploaded[0].blob_uri);
      }
    },
    [currentDocument.id, currentDocument.type, invoiceAttachmentsQuery, uploadInvoiceAttachments],
  );

  const handleDeleteInvoiceAttachment = useCallback(
    async (attachmentId: string) => {
      if (currentDocument.type !== 'invoice' || !currentDocument.id) {
        return;
      }
      await deleteInvoiceAttachment.mutateAsync({
        invoiceId: currentDocument.id,
        attachmentId,
      });
      await invoiceAttachmentsQuery.refetch();
    },
    [currentDocument.id, currentDocument.type, deleteInvoiceAttachment, invoiceAttachmentsQuery],
  );

  const handleSubmitCorrections = async () => {
    try {
      // Get edited data from the editable fields component
      const editedData = editableFieldsRef.current?.getEditedData() || {};
      if (showLineItemsSection) {
        editedData.lines = lineItems;
      }

      // Build corrections - use top-level fields if available, otherwise use extraction data
      let corrections: Record<string, any> = {};

      if (extractionStatusQuery.data?.document) {
        // New approach: use top-level document fields
        const documentData: DocumentFieldsResponse = {
          id: extractionStatusQuery.data.document.id,
          extraction_status: extractionStatusQuery.data.document.extraction_status,
          match_status: extractionStatusQuery.data.document.match_status,
          ...editedData,
        };
        corrections = extractCorrectionsFromTopLevel(documentData);
      } else {
        // Fallback: use extraction data
        corrections = extractCorrections(editedData);
      }

      const dynamicErpFieldPayload = dynamicErpValuesChanged && pushableDynamicErpValues.length > 0
        ? pushableDynamicErpValues
        : undefined;

      if (Object.keys(corrections).length === 0 && !dynamicErpFieldPayload) {
        console.warn('No corrections detected');
        return;
      }

      if (!currentDocument.id) throw new Error('Document ID is required');
      if (!currentUser?.id) throw new Error('Current user is required');

      // Run mismatch checks before submitting.
      const doc = extractionStatusQuery.data?.document;
      const checkInput: MismatchCheckInput = {
        lineItems: showLineItemsSection ? lineItems : [],
        subtotal: editedData.subtotal ?? doc?.subtotal,
        docTotal: editedData.total ?? doc?.total,
        taxTotal: editedData.tax_total ?? doc?.tax_total,
        discountTotal: editedData.discount_total ?? doc?.discount_total,
        additionalCharges: editedData.additional_charges ?? doc?.additional_charges,
      };
      const issues = detectLineMismatches(checkInput);
      if (issues.length > 0) {
        pendingMismatchAction.current = {
          action: 'corrections',
          documentType: currentDocument.type,
          documentId: currentDocument.id,
          user: currentUser.id,
          notes: correctionNotes || undefined,
          corrections,
          dynamic_erp_fields: dynamicErpFieldPayload,
          erp_connection_id: selectedErpConnectionId || undefined,
        };
        setMismatchIssues(issues);
        setShowMismatchDialog(true);
        return;
      }

      await submitCorrections.mutateAsync({
        documentType: currentDocument.type,
        documentId: currentDocument.id,
        user: currentUser.id,
        corrections,
        notes: correctionNotes || undefined,
        dynamic_erp_fields: dynamicErpFieldPayload,
        erp_connection_id: selectedErpConnectionId || undefined,
      });
      await extractionStatusQuery.refetch();
      setShowCorrectionForm(false);
      setHeaderMatchingRelevantChange(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Corrections submission failed:', error);
    }
  };

  const handleApplyFlagAction = useCallback(
    async (flag: DocumentFlagInfo, actionType: string, payload?: Record<string, any>) => {
      try {
        if (!currentDocument.id) throw new Error('Document ID is required');
        if (!flag.id) throw new Error('Flag ID is required');
        if (!currentUser?.id) throw new Error('Current user is required');

        await applyDocumentFlagAction.mutateAsync({
          documentType: currentDocument.type,
          documentId: currentDocument.id,
          flagId: flag.id,
          request: {
            user: currentUser.id,
            action_type: actionType,
            payload,
          },
        });

        await Promise.all([
          extractionStatusQuery.refetch(),
          missingReferencesQuery.refetch(),
        ]);
      } catch (error) {
        console.error('Failed to apply flag action:', error);
      }
    },
    [
      applyDocumentFlagAction,
      currentDocument.id,
      currentDocument.type,
      currentUser?.id,
      extractionStatusQuery,
      missingReferencesQuery,
    ]
  );

  // Determine document status
  const extractionStatus = extractionStatusQuery.data?.extraction_status || currentDocument.extraction_status || 'pending_review';
  const isApproved = extractionStatus === 'approved';
  const isRejected = extractionStatus === 'rejected';
  const approvalHistoryRejectionReason = useMemo(() => {
    const rejectedDecision = (approvalHistoryQuery.data || [])
      .filter((event) => event.event_type === 'decision' && event.decision === 'rejected' && event.comments?.trim())
      .sort((left, right) => {
        const leftTime = new Date(left.responded_at || left.created_at).getTime();
        const rightTime = new Date(right.responded_at || right.created_at).getTime();
        return rightTime - leftTime;
      })[0];
    return rejectedDecision?.comments?.trim() || '';
  }, [approvalHistoryQuery.data]);
  const rejectionDisplayReason =
    extractionStatusQuery.data?.document?.review_notes ||
    currentDocument.review_notes ||
    approvalHistoryRejectionReason;
  const canSubmitCorrections = ['extracted', 'pending_review', 'approved'].includes(extractionStatus);
  const approvalStatus = approvalRequestQuery.data?.status ?? extractionStatusQuery.data?.approval_status ?? null;
  const selectedDocumentErpConfigs = documentErpConfigQuery.data?.filter((config) => config.enabled);
  const dynamicReviewFields = selectedDocumentErpConfigs?.length
    ? selectedDocumentErpConfigs
    : extractionStatusQuery.data?.dynamic_erp_fields?.fields || [];
  const pushableDynamicReviewFields = dynamicReviewFields.filter(isBillPushableReviewConfig);
  const pushableDynamicReviewFieldKeys = new Set(
    pushableDynamicReviewFields.map((config) => config.field.field_key),
  );
  const pushableDynamicErpValues = dynamicErpValues.filter(
    (value) => pushableDynamicReviewFieldKeys.has(value.field_key) || isReviewErpFieldKey(value.field_key),
  );
  const originalDynamicErpValues = extractionStatusQuery.data?.dynamic_erp_fields?.values || [];
  const originalPushableDynamicErpValues = originalDynamicErpValues.filter(
    (value) => pushableDynamicReviewFieldKeys.has(value.field_key) || isReviewErpFieldKey(value.field_key),
  );
  const dynamicErpValuesChanged = JSON.stringify(pushableDynamicErpValues) !== JSON.stringify(originalPushableDynamicErpValues);
  const currentHeaderNumber = useMemo(() => {
    const liveDocument = extractionStatusQuery.data?.document;
    if (currentDocument.type === 'invoice') {
      return liveDocument?.external_invoice_number || currentDocument.external_number || '';
    }
    if (currentDocument.type === 'receipt') {
      return liveDocument?.external_receipt_number || currentDocument.external_number || '';
    }
    return liveDocument?.po_number || currentDocument.po_number || currentDocument.external_number || '';
  }, [
    currentDocument.external_number,
    currentDocument.po_number,
    currentDocument.type,
    extractionStatusQuery.data?.document,
  ]);
  const headerHasChanges =
    headerDraftType !== currentDocument.type || headerDraftNumber.trim() !== String(currentHeaderNumber || '').trim();

  useEffect(() => {
    setHeaderDraftType(currentDocument.type);
    setHeaderDraftNumber(String(currentHeaderNumber || ''));
  }, [currentDocument.id, currentDocument.type, currentHeaderNumber]);

  const handleSaveErpConnection = useCallback(async () => {
    if (
      !currentDocument.id ||
      !currentUser?.id ||
      !selectedErpConnectionId ||
      !erpConnectionHasChanges
    ) {
      return;
    }

    try {
      await submitCorrections.mutateAsync({
        documentType: currentDocument.type,
        documentId: currentDocument.id,
        user: currentUser.id,
        corrections: {},
        erp_connection_id: selectedErpConnectionId,
      });
      setCurrentDocument((previous) => ({
        ...previous,
        erp_connection_id: selectedErpConnectionId,
      }));
      await extractionStatusQuery.refetch();
    } catch (error) {
      console.error('ERP link save failed:', error);
    }
  }, [
    currentDocument.id,
    currentDocument.type,
    currentUser?.id,
    erpConnectionHasChanges,
    extractionStatusQuery,
    selectedErpConnectionId,
    submitCorrections,
  ]);

  const handleSaveHeader = useCallback(async () => {
    if (!currentDocument.id || !currentUser?.id || !headerHasChanges) {
      return;
    }
    const response = await updateDocumentHeader.mutateAsync({
      documentType: currentDocument.type,
      documentId: currentDocument.id,
      payload: {
        user: currentUser.id,
        document_type: headerDraftType,
        document_number: headerDraftNumber.trim() || null,
      },
    });
    setCurrentDocument((prev) => ({
      ...prev,
      id: response.document_id,
      type: response.document_type,
      external_number: response.document_number || prev.external_number,
      po_number: response.document_type === 'purchase_order'
        ? response.document_number || undefined
        : prev.po_number,
    }));
    setHeaderDraftType(response.document_type);
    setHeaderDraftNumber(response.document_number || '');
    await Promise.all([
      extractionStatusQuery.refetch(),
      missingReferencesQuery.refetch(),
      relatedAPPostingsQuery.refetch(),
    ]);
    setHeaderMatchingRelevantChange(false);
    setRefreshKey((prev) => prev + 1);
  }, [
    currentDocument.id,
    currentDocument.type,
    currentUser?.id,
    extractionStatusQuery,
    headerDraftNumber,
    headerDraftType,
    headerHasChanges,
    missingReferencesQuery,
    relatedAPPostingsQuery,
    updateDocumentHeader,
  ]);
  const dynamicReviewPayload = {
    erp_connection_id: selectedErpConnectionId || extractionStatusQuery.data?.dynamic_erp_fields?.erp_connection_id,
    fields: pushableDynamicReviewFields,
    values: dynamicErpValues,
  };
  const documentErpCatalogFields = documentErpCatalogQuery.data?.fields || [];
  const documentErpConfigByFieldId = new Map(
    (documentErpConfigQuery.data || []).map((config) => [config.field_definition_id, config]),
  );
  const enabledDocumentErpFieldIds = new Set(
    (documentErpConfigQuery.data || [])
      .filter((config) => config.enabled)
      .map((config) => config.field_definition_id),
  );
  const availableReviewErpFields = getAvailableReviewErpFields(
    documentErpCatalogFields,
    enabledDocumentErpFieldIds,
  );
  const availableHeaderErpFields = availableReviewErpFields.filter((field) => field.value_scope === 'header');
  const availableLineErpFields = availableReviewErpFields.filter((field) => field.value_scope === 'line');
  const enabledDynamicLineFieldConfigs = pushableDynamicReviewFields.filter(
    (config) => config.field.value_scope === 'line',
  );
  const hasLineAccountConfig = enabledDynamicLineFieldConfigs.some(
    (config) => erpFieldKeyTail(config.field.field_key) === 'line_account',
  );
  const lineAccountCatalogField = documentErpCatalogFields.find(
    (field) => field.value_scope === 'line' && erpFieldKeyTail(field.field_key) === 'line_account',
  );
  const lineAccountExistingConfig = lineAccountCatalogField
    ? documentErpConfigByFieldId.get(lineAccountCatalogField.id)
    : undefined;
  const lineAccountFallbackConfig: TenantErpFieldConfig | undefined =
    !hasLineAccountConfig && lineAccountCatalogField
      ? {
          id: lineAccountExistingConfig?.id || `catalog-${lineAccountCatalogField.id}`,
          tenant_id: lineAccountCatalogField.tenant_id,
          field_definition_id: lineAccountCatalogField.id,
          surface: 'document_review',
          enabled: true,
          display_order: lineAccountExistingConfig?.display_order ?? enabledDynamicLineFieldConfigs.length,
          required: lineAccountExistingConfig?.required ?? false,
          memory_enabled: lineAccountExistingConfig?.memory_enabled ?? false,
          automation_mode: lineAccountExistingConfig?.automation_mode ?? 'off',
          extra: {
            ...(lineAccountExistingConfig?.extra || {}),
            embedded_line_type_picker: true,
          },
          field: lineAccountCatalogField,
          created_at: lineAccountCatalogField.created_at,
          updated_at: lineAccountExistingConfig?.updated_at ?? lineAccountCatalogField.updated_at,
        }
      : undefined;
  const dynamicLineFieldConfigs = lineAccountFallbackConfig
    ? [...enabledDynamicLineFieldConfigs, lineAccountFallbackConfig]
    : enabledDynamicLineFieldConfigs;
  const dynamicHeaderFieldCount = pushableDynamicReviewFields.filter(
    (config) => config.field.value_scope === 'header',
  ).length;

  const buildDocumentReviewConfigPayload = useCallback((
    fieldId: string,
    updates: Partial<TenantErpFieldConfigItem>,
  ): TenantErpFieldConfigItem[] => {
    return documentErpCatalogFields.map((field, index) => {
      const existing = documentErpConfigByFieldId.get(field.id);
      const base: TenantErpFieldConfigItem = {
        field_definition_id: field.id,
        surface: 'document_review',
        enabled: existing?.enabled ?? false,
        display_order: existing?.display_order ?? index,
        required: existing?.required ?? field.required_default,
        memory_enabled: existing?.memory_enabled ?? false,
        automation_mode: existing?.automation_mode ?? 'off',
        extra: existing?.extra ?? {},
      };
      if (field.id !== fieldId) {
        return base;
      }
      return {
        ...base,
        ...updates,
        enabled: true,
        memory_enabled: true,
        automation_mode: 'suggest',
      };
    });
  }, [documentErpCatalogFields, documentErpConfigByFieldId]);

  const handleAddDocumentErpField = useCallback(async (fieldId: string, scope: 'header' | 'line') => {
    if (!fieldId) {
      return;
    }
    await updateErpFieldConfig.mutateAsync(buildDocumentReviewConfigPayload(fieldId, {
      enabled: true,
      memory_enabled: true,
      automation_mode: 'suggest',
    }));
    if (scope === 'header') {
      setSelectedHeaderErpFieldId('');
    } else {
      setSelectedLineErpFieldId('');
    }
    await Promise.all([
      documentErpConfigQuery.refetch(),
      extractionStatusQuery.refetch(),
    ]);
  }, [buildDocumentReviewConfigPayload, documentErpConfigQuery, extractionStatusQuery, updateErpFieldConfig]);
  const hasApprovalChainView = Boolean(approvalRequestId);
  const documentPermissionState = getDocumentPermissionState({
    currentUser,
    hasPermission,
    approvalRequest: approvalRequestQuery.data,
    approvalHistory: approvalHistoryQuery.data,
  });
  const isResolvingApprovalPermissions =
    hasApprovalChainView && (approvalRequestQuery.isLoading || approvalHistoryQuery.isLoading);

  const permissionBannerMessage = isResolvingApprovalPermissions
    ? 'Checking document permissions for the active approval chain.'
    : documentPermissionState.viewOnlyReason;

  const approveLockReason = isResolvingApprovalPermissions
    ? 'Checking document permissions for the active approval chain.'
    : hasBlockingIssues
      ? 'Resolve blocking issues before approving.'
      : documentPermissionState.approveLockReason;
  const rejectLockReason = isResolvingApprovalPermissions
    ? 'Checking document permissions for the active approval chain.'
    : isRejected
      ? 'This document has already been rejected.'
    : documentPermissionState.rejectLockReason;
  const deleteLockReason = isResolvingApprovalPermissions
    ? 'Checking document permissions for the active approval chain.'
    : documentPermissionState.deleteLockReason;
  const correctionsLockReason = isResolvingApprovalPermissions
    ? 'Checking document permissions for the active approval chain.'
    : isRejected
      ? 'Rejected documents cannot be corrected.'
    : documentPermissionState.correctionsLockReason;

  const renderLockedLabel = (label: string, locked: boolean) => (
    <span className="inline-flex items-center gap-2">
      {locked ? <Lock className="h-4 w-4" /> : null}
      {label}
    </span>
  );
  const isExtractionStage = activeStage === 'extraction';
  const isApprovalProgressStage = activeStage === 'approval_progress';
  const isAPPostingStage = activeStage === 'ap_posting';
  const isPaymentStage = activeStage === 'payment';
  const documentTypeTitle =
    currentDocument.type === "purchase_order"
      ? 'Purchase Order'
      : currentDocument.type === 'receipt'
        ? 'Receipt'
        : 'Invoice';
  const formatCurrency = (value: number | string, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(typeof value === 'number' ? value : Number(value || 0));
  const canSubmitExistingPaymentOrder = (status?: string) =>
    status === 'draft' || status === 'scheduled' || status === 'failed';
  const selectedPostingPaymentReady =
    Boolean(selectedAPPosting) &&
    (selectedAPPosting?.status === 'approved' || selectedAPPosting?.status === 'posted');
  const tenantStripeReady = stripeConnection?.status === 'ready';
  const vendorPayoutReady = selectedVendorProfile?.status === 'ready';
  const canShowApprovalProgressStage = hasApprovalChainView || currentDocument.type === 'invoice';
  const canShowAPPostingStage =
    currentDocument.type === 'invoice' &&
    (isApproved || relatedAPPostings.length > 0 || relatedAPPostingsQuery.isFetching);
  const canShowPaymentStage =
    currentDocument.type === 'invoice' &&
    Boolean(selectedAPPosting) &&
    (selectedPostingPaymentReady || Boolean(selectedPaymentOrder));

  const renderStageButton = (
    stage: ReviewStage,
    label: string,
    disabled = false,
  ) => (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setActiveStage(stage)}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        activeStage === stage
          ? 'bg-blue-600 text-white'
          : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100'
      }`}
    >
      {label}
    </button>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-[95vw] max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Review {documentTypeTitle}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6" key={refreshKey}>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {isExtractionStage
                  ? 'Extraction review'
                  : isApprovalProgressStage
                    ? 'Approval progress'
                    : isAPPostingStage
                      ? 'AP posting review'
                      : 'Payment'}
              </p>
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Type</span>
                  <SearchableSelect
                    value={headerDraftType}
                    onChange={(value) => setHeaderDraftType(value as ReviewDocumentType)}
                    disabled={updateDocumentHeader.isPending}
                    className="w-40"
                    options={REVIEW_DOCUMENT_TYPE_OPTIONS}
                    placeholder="Type"
                    clearable={false}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Number</span>
                  <input
                    type="text"
                    value={headerDraftNumber}
                    onChange={(event) => setHeaderDraftNumber(event.target.value)}
                    disabled={updateDocumentHeader.isPending}
                    placeholder={currentDocument.id ? `#${currentDocument.id}` : undefined}
                    className="w-48 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
                {showErpConnectionControl && (
                  <div className="block max-w-md">
                    <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">ERP company</span>
                    <div className="flex items-center gap-2">
                      <SearchableSelect
                        value={selectedErpConnectionId}
                        onChange={(value) => {
                          setSelectedErpConnectionId(value);
                          setDynamicErpValues([]);
                          setSelectedHeaderErpFieldId('');
                          setSelectedLineErpFieldId('');
                          setLineItems((current) => current.map((line) => ({
                            ...line,
                            erp_item_id: null,
                            erp_item_name: null,
                            erp_account_id: null,
                            erp_account_name: null,
                          })));
                        }}
                        className="w-56"
                        options={[
                          { id: '', label: 'Choose ERP company' },
                          ...activeErpConnections.map((connection) => ({
                            id: connection.id,
                            label: connection.display_name || connection.external_company_name || `${connection.erp_type} ${connection.realm_id}`,
                          })),
                        ]}
                        placeholder="Choose ERP company"
                        clearable={false}
                        aria-label="ERP company"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={
                          !erpConnectionHasChanges ||
                          submitCorrections.isPending ||
                          Boolean(correctionsLockReason)
                        }
                        onClick={handleSaveErpConnection}
                      >
                        {submitCorrections.isPending ? 'Saving...' : 'Save ERP Link'}
                      </Button>
                    </div>
                    {!hasActivePersistedErpConnection && hasBlockingIssues && (
                      <p className="mt-1 text-xs text-content-muted">
                        You can save the ERP link while the missing reference remains open.
                      </p>
                    )}
                    {extractionStatusQuery.data?.dynamic_erp_fields?.connection_resolution?.source === 'auto' && (
                      <p className="mt-1 text-xs text-content-muted">
                        Auto-selected ({extractionStatusQuery.data.dynamic_erp_fields.connection_resolution.confidence}):{' '}
                        {extractionStatusQuery.data.dynamic_erp_fields.connection_resolution.reasons[0]}
                      </p>
                    )}
                  </div>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!headerHasChanges || updateDocumentHeader.isPending}
                  onClick={handleSaveHeader}
                >
                  {updateDocumentHeader.isPending ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-900">
              {renderStageButton('extraction', 'Extraction')}
              {renderStageButton('approval_progress', 'Approval Progress', !canShowApprovalProgressStage)}
              {renderStageButton('ap_posting', 'AP Posting', !canShowAPPostingStage)}
              {renderStageButton('payment', 'Payment', !canShowPaymentStage)}
            </div>
          </div>

          {isApprovalProgressStage && (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Document Review Approval
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {documentTypeTitle} {currentHeaderNumber || `#${currentDocument.id}`}
                    </p>
                  </div>
                  <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium capitalize text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                    {(approvalStatus || extractionStatus).replace(/_/g, ' ')}
                  </span>
                </div>
                {approvalRequestId ? (
                  <ApprovalChainReviewUI
                    approvalRequestId={approvalRequestId}
                    extractionId={currentDocument.id || ''}
                    onApprovalComplete={handleApprovalChainComplete}
                    onRejected={handleApprovalChainRejected}
                    onClose={() => setActiveStage('extraction')}
                  />
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No document approval chain is currently active.
                  </p>
                )}
              </div>

              {currentDocument.type === 'invoice' && (
                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        AP Posting Approval
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {selectedAPPosting ? `Invoice ${selectedAPPosting.invoice_num}` : 'Linked AP posting'}
                      </p>
                    </div>
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium capitalize text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                      {apPostingDisplayStatus.replace(/_/g, ' ')}
                    </span>
                  </div>

                  <APPostingErrorAlert error={apPostingError} />

                  {relatedAPPostings.length > 1 && (
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      {relatedAPPostings.map((posting) => (
                        <button
                          key={posting.id}
                          type="button"
                          onClick={() => setSelectedAPPostingId(posting.id)}
                          className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                            selectedAPPosting?.id === posting.id
                              ? 'border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-200'
                              : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-900'
                          }`}
                        >
                          {posting.invoice_num} · {posting.status.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedAPPosting ? (
                    <div className="space-y-3">
                      {apPostingApprovalRequestId ? (
                        <ApprovalChainReviewUI
                          approvalRequestId={apPostingApprovalRequestId}
                          extractionId={selectedAPPosting.id}
                          onApprovalComplete={handleAPPostingApprovalChainComplete}
                          onRejected={handleAPPostingApprovalChainRejected}
                          onClose={() => setActiveStage('ap_posting')}
                          entityLabel="AP posting"
                          entityTitle="AP Posting"
                        />
                      ) : (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          No AP posting approval chain is currently active.
                        </p>
                      )}
                      <Button
                        variant="secondary"
                        onClick={() => setActiveStage('ap_posting')}
                      >
                        View AP Posting
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
                      {relatedAPPostingsQuery.isFetching
                        ? 'Preparing linked AP posting review...'
                        : 'The linked AP posting review will appear after invoice approval completes.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {isAPPostingStage && (
            <div className="space-y-4">
              {relatedAPPostings.length > 1 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    AP postings
                  </span>
                  {relatedAPPostings.map((posting) => (
                    <button
                      key={posting.id}
                      type="button"
                      onClick={() => setSelectedAPPostingId(posting.id)}
                      className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                        selectedAPPosting?.id === posting.id
                          ? 'border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-200'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-800'
                      }`}
                    >
                      {posting.invoice_num} · {posting.status.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              )}

              {selectedAPPosting ? (
                <APPostingApprovalModal
                  posting={selectedAPPosting}
                  isOpen={true}
                  onClose={() => setActiveStage('approval_progress')}
                  onReviewComplete={handleAPPostingReviewComplete}
                  variant="embedded"
                />
              ) : (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
                  {relatedAPPostingsQuery.isFetching
                    ? 'Preparing linked AP posting review...'
                    : 'The linked AP posting review will appear after invoice approval completes.'}
                </div>
              )}
            </div>
          )}

          {isPaymentStage && (
            <div className="space-y-4">
              {selectedAPPosting ? (
                <>
                  <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          Payment for AP Posting
                        </h3>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                          {selectedAPPosting.vendor_name || selectedAPPosting.vendor_name_extracted || 'Vendor'} · Invoice {selectedAPPosting.invoice_num}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Amount</p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(selectedAPPosting.posting_amount, selectedAPPosting.currency)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-3">
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900">
                        <p className="font-medium text-gray-700 dark:text-gray-300">AP posting</p>
                        <p className="mt-1 capitalize text-gray-600 dark:text-gray-400">
                          {selectedAPPosting.status.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900">
                        <p className="font-medium text-gray-700 dark:text-gray-300">Tenant Stripe</p>
                        <p className="mt-1 capitalize text-gray-600 dark:text-gray-400">
                          {(stripeConnection?.status || 'not_started').replace(/_/g, ' ')}
                        </p>
                      </div>
                      <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900">
                        <p className="font-medium text-gray-700 dark:text-gray-300">Vendor payout</p>
                        <p className="mt-1 capitalize text-gray-600 dark:text-gray-400">
                          {(selectedVendorProfile?.status || 'not_started').replace(/_/g, ' ')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedPaymentOrder ? (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                            Payment order {selectedPaymentOrder.status.replace(/_/g, ' ')}
                          </p>
                          <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">
                            {formatCurrency(selectedPaymentOrder.amount, selectedPaymentOrder.currency)}
                            {selectedPaymentOrder.scheduled_for
                              ? ` · scheduled ${new Date(selectedPaymentOrder.scheduled_for).toLocaleDateString()}`
                              : ''}
                          </p>
                          {selectedPaymentOrder.failure_reason && (
                            <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                              {selectedPaymentOrder.failure_reason}
                            </p>
                          )}
                        </div>
                        {canSubmitExistingPaymentOrder(selectedPaymentOrder.status) && (
                          <Button
                            type="button"
                            onClick={() => handleSubmitExistingPaymentOrder(selectedPaymentOrder.id)}
                            disabled={submitPaymentOrder.isPending}
                          >
                            {submitPaymentOrder.isPending
                              ? 'Submitting...'
                              : selectedPaymentOrder.status === 'scheduled'
                                ? 'Submit Now'
                                : 'Submit Payment'}
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                      {!selectedPostingPaymentReady && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                          Approve the AP posting before creating a payment.
                        </div>
                      )}
                      {selectedPostingPaymentReady && !tenantStripeReady && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                          Tenant Stripe connection is not ready.
                        </div>
                      )}
                      {selectedPostingPaymentReady && tenantStripeReady && !vendorPayoutReady && (
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                          <div>
                            <p className="font-medium">Vendor payout setup is not ready.</p>
                            <p className="mt-1 text-xs">
                              Start a vendor payment setup request before creating this payment.
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={handleStartVendorPaymentRequest}
                            disabled={!selectedAPPosting.vendor_id || startVendorPaymentProfile.isPending}
                          >
                            {startVendorPaymentProfile.isPending
                              ? 'Starting...'
                              : selectedVendorProfile?.status === 'invited' || selectedVendorProfile?.status === 'pending_vendor_action'
                                ? 'Resend Setup Request'
                                : 'Start Setup Request'}
                          </Button>
                        </div>
                      )}

                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Schedule Date
                        <input
                          type="date"
                          value={scheduleDate}
                          onChange={(event) => setScheduleDate(event.target.value)}
                          className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                        />
                      </label>

                      <div className="flex flex-wrap justify-end gap-3">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => handleCreatePayment('scheduled')}
                          disabled={
                            !scheduleDate ||
                            !selectedPostingPaymentReady ||
                            !tenantStripeReady ||
                            !vendorPayoutReady ||
                            createPaymentOrder.isPending ||
                            submitPaymentOrder.isPending
                          }
                        >
                          Schedule
                        </Button>
                        <Button
                          type="button"
                          onClick={() => handleCreatePayment('manual')}
                          disabled={
                            !selectedPostingPaymentReady ||
                            !tenantStripeReady ||
                            !vendorPayoutReady ||
                            createPaymentOrder.isPending ||
                            submitPaymentOrder.isPending
                          }
                        >
                          {createPaymentOrder.isPending || submitPaymentOrder.isPending ? 'Submitting...' : 'Pay Now'}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
                  Select or approve an AP posting before creating a payment.
                </div>
              )}
            </div>
          )}

          {/* Document Review Content */}
          {isExtractionStage && (
            <>
              {hasApprovalChainView && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
                  {approvalStatus === 'approved'
                    ? 'This document completed its approval chain.'
                    : approvalStatus === 'rejected'
                    ? 'This document was rejected in its approval chain.'
                  : 'This document is currently in an approval chain.'}
                </div>
              )}

              {isRejected && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
                  <p className="font-semibold">This document was rejected.</p>
                  <p className="mt-1">
                    Reason: {rejectionDisplayReason || 'No reason was provided.'}
                  </p>
                </div>
              )}

              {permissionBannerMessage && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                  {permissionBannerMessage}
                </div>
              )}

              {/* Missing References Alert */}
              {missingReferences.length > 0 && (
                <MissingReferencesAlert
                  missingReferences={missingReferences}
                  hasBlockingIssues={hasBlockingIssues}
                />
              )}

          {/* Missing References Panel */}
          {missingReferences.length > 0 && (
            <MissingReferencesPanel
              missingReferences={missingReferences}
              document={currentDocument}
              documentData={extractionStatusQuery.data?.document}
              extractionData={extractionStatusQuery.data?.extraction_data || currentDocument.extraction_data}
              onReferencesResolved={handleReferencesResolved}
            />
          )}

          {/* Document Flags */}
          {currentDocumentFlags.length > 0 && (
            <DocumentFlagsPanel
              flags={currentDocumentFlags}
              onApplyAction={handleApplyFlagAction}
              isApplyingAction={applyDocumentFlagAction.isPending}
            />
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Document Number</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {currentHeaderNumber || `#${currentDocument.id}`}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Confidence Score</p>
              <div className="mt-2">
                <ConfidenceScore score={currentDocument.confidence} />
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Vendor</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {currentDocument.vendor_name || currentDocument.vendor_name_extracted || '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 capitalize">
                {extractionStatus.replace(/_/g, ' ')}
              </p>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowMatchingPanel((prev) => !prev)}
                  className="max-w-full text-left"
                >
                  <MatchStatusBadge document={currentMatchDocument} showDetail />
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4 dark:border-blue-900 dark:bg-blue-950/30">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Posting Coding Suggestions
                </h3>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Capture only ERP coding that can post to bills. Line-level fields stay with their document lines unless the bill is posted as a single header-only expense.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <SearchableSelect
                  value={selectedHeaderErpFieldId}
                  onChange={setSelectedHeaderErpFieldId}
                  options={[
                    { id: '', label: 'Header coding...' },
                    ...availableHeaderErpFields.map((field) => ({ id: field.id, label: field.label })),
                  ]}
                  placeholder="Header coding..."
                  clearable={false}
                  className="w-48"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!selectedHeaderErpFieldId || updateErpFieldConfig.isPending}
                  onClick={() => handleAddDocumentErpField(selectedHeaderErpFieldId, 'header')}
                  className="inline-flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Header Suggestion
                </Button>
              </div>
            </div>
            <DynamicErpFieldsEditor
              payload={dynamicReviewPayload}
              values={dynamicErpValues}
              onChange={setDynamicErpValues}
              surface="document_review"
              valueScope="header"
              showDescriptions
            />
            {dynamicHeaderFieldCount === 0 && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Add bill-level coding such as payment terms, department, or location when it is useful during review.
              </p>
            )}
          </div>

          {matchingWillRecalculate && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
              Matching will be recalculated after save.
            </div>
          )}

          {showMatchingPanel && (
            <MatchingPanel
              documentType={docType}
              documentId={currentDocument.id}
              documentData={extractionStatusQuery.data?.document}
              onRefresh={async () => {
                await extractionStatusQuery.refetch();
                setRefreshKey((prev) => prev + 1);
              }}
            />
          )}

          {/* Split View: Document Viewer + Extracted Fields */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Original Document */}
            {currentDocument.blob_uri && (
              <div className="flex flex-col">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Original Document
                </h3>
                <div className="flex-1 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <DocumentViewer
                    blobUri={currentDocument.blob_uri}
                    className="h-[400px] lg:h-[500px]"
                  />
                </div>
              </div>
            )}

            {/* Extracted Data (without line items) */}
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Extracted Data
              </h3>
              <div className="overflow-y-auto bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 h-[400px] lg:h-[500px]">
                {extractionStatusQuery.data?.document ? (
                  <EditableDocumentFields
                    ref={editableFieldsRef}
                    documentData={extractionStatusQuery.data.document}
                    perFieldConf={perFieldConf}
                    confidenceThreshold={0.9}
                    excludeLineItems
                    documentType={docType}
                    selectedFields={selectedHeaderFields}
                    fieldCatalog={adminExtractionFieldCatalog}
                    taxIdTypes={extractionStatusQuery.data.tax_id_types || []}
                    onMatchingRelevantChange={setHeaderMatchingRelevantChange}
                    erpConnectionId={selectedErpConnectionId || undefined}
                  />
                ) : (
                  <EditableExtractedFields
                    ref={editableFieldsRef}
                    document={currentDocument}
                    perFieldConf={perFieldConf}
                    confidenceThreshold={0.9}
                    excludeLineItems
                    documentType={docType}
                    selectedFields={selectedHeaderFields}
                    fieldCatalog={adminExtractionFieldCatalog}
                    erpConnectionId={selectedErpConnectionId || undefined}
                  />
                )}
              </div>
            </div>
          </div>

          {currentDocument.type === 'invoice' && currentDocument.id && (
            <InvoiceAttachmentsPanel
              attachments={invoiceAttachmentsQuery.data || []}
              isLoading={invoiceAttachmentsQuery.isLoading}
              isUploading={uploadInvoiceAttachments.isPending}
              isDeleting={deleteInvoiceAttachment.isPending}
              selectedBlobUri={selectedAttachmentBlobUri}
              onSelectBlobUri={setSelectedAttachmentBlobUri}
              onUpload={handleUploadInvoiceAttachments}
              onDelete={handleDeleteInvoiceAttachment}
            />
          )}

          {showLineItemsSection && (
            <div className="flex min-h-[520px] flex-1 flex-col">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Line Items
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  {addableLineItemFieldOptions.length > 0 && (
                    <>
                      <SearchableSelect
                        value={selectedLineItemFieldToAdd}
                        onChange={setSelectedLineItemFieldToAdd}
                        options={[
                          { id: '', label: 'Line field...' },
                          ...addableLineItemFieldOptions.map((field) => ({ id: field.fieldKey, label: field.label })),
                        ]}
                        placeholder="Line field..."
                        clearable={false}
                        className="w-44"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={!selectedLineItemFieldToAdd || isLoading}
                        onClick={handleAddLineItemField}
                        className="inline-flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Line Field
                      </Button>
                    </>
                  )}
                  <SearchableSelect
                    value={selectedLineErpFieldId}
                    onChange={setSelectedLineErpFieldId}
                    options={[
                      { id: '', label: 'Line coding...' },
                      ...availableLineErpFields.map((field) => ({ id: field.id, label: field.label })),
                    ]}
                    placeholder="Line coding..."
                    clearable={false}
                    className="w-44"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!selectedLineErpFieldId || updateErpFieldConfig.isPending}
                    onClick={() => handleAddDocumentErpField(selectedLineErpFieldId, 'line')}
                    className="inline-flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Coding Column
                  </Button>
                </div>
              </div>
              <LineItemsEditor
                erpConnectionId={selectedErpConnectionId || undefined}
                billDetailProvider={selectedErpConnection?.erp_type}
                items={lineItems}
                onChange={handleModalLineItemsChange}
                documentType={currentDocument.type}
                selectedLineItemFields={selectedLineItemFields}
                dynamicErpFieldConfigs={dynamicLineFieldConfigs}
                dynamicErpValues={dynamicErpValues}
                onDynamicErpValuesChange={setDynamicErpValues}
                fillAvailableHeight
                className="min-h-0 flex-1"
              />
            </div>
          )}

          {/* Review Notes */}
          {!isRejected && currentDocument.review_notes && (
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Review Notes
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{currentDocument.review_notes}</p>
            </div>
          )}

          {/* Rejection Form */}
          {showRejectForm && !isApproved && !isRejected && (
            <div className="bg-red-50 dark:bg-red-950 p-4 rounded-lg space-y-3">
              <label className="block">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Reason for Rejection
                </p>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                  rows={3}
                />
              </label>
            </div>
          )}

          {/* Corrections Form (for approved documents) */}
          {showCorrectionForm && isApproved && (
            <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg space-y-3">
              <label className="block">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Correction Notes (Optional)
                </p>
                <textarea
                  value={correctionNotes}
                  onChange={(e) => setCorrectionNotes(e.target.value)}
                  placeholder="Enter optional notes about these corrections..."
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                  rows={3}
                />
              </label>
            </div>
          )}

          {/* Status badge */}
          {!showCorrectionForm && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium ${
              isApproved
                ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300'
                : isRejected
                  ? 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300'
                : 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300'
            }`}>
              <span>
                {isApproved ? '✓ Document Approved' :
                 isRejected ? `Document Rejected${rejectionDisplayReason ? ` - ${rejectionDisplayReason}` : ''}` :
                 extractionStatus === 'extracted' ? '⚙ Extraction Complete - Pending Review' :
                 extractionStatus === 'pending_review' ? '⏳ Pending Human Review' :
                 'Unknown Status'}
                </span>
            </div>
          )}

            </>
          )}
        </div>

        {/* Footer */}
        {isExtractionStage && (
        <div className="sticky bottom-0 flex gap-3 justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          {/* Delete button on the left */}
          {!showRejectForm && !showDeleteConfirm && (
            <Button
              variant="secondary"
              disabled={isLoading || Boolean(deleteLockReason)}
              onClick={() => setShowDeleteConfirm(true)}
              className="text-red-600 hover:text-red-700 dark:text-red-400 hover:dark:text-red-300 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {renderLockedLabel('Delete', Boolean(deleteLockReason))}
            </Button>
          )}

          {/* Delete confirmation buttons */}
          {showDeleteConfirm && (
            <div className="flex gap-3">
              <Button
                variant="secondary"
                disabled={isLoading}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={isLoading || Boolean(deleteLockReason)}
                onClick={handleDelete}
              >
                {isLoading ? 'Deleting...' : renderLockedLabel('Confirm Delete', Boolean(deleteLockReason))}
              </Button>
            </div>
          )}

          {!showDeleteConfirm && (
            <div className="flex gap-3">
              <Button
                variant="secondary"
                disabled={isLoading}
                onClick={onClose}
              >
                Cancel
              </Button>

              {showCorrectionForm && canSubmitCorrections ? (
                // CORRECTIONS FORM SHOWN: Confirm or cancel corrections
                <>
                  <Button
                    variant="secondary"
                    disabled={isLoading}
                    onClick={() => setShowCorrectionForm(false)}
                  >
                    Back
                  </Button>
                  <Button
                    disabled={isLoading || Boolean(correctionsLockReason)}
                    onClick={handleSubmitCorrections}
                  >
                    {isLoading ? 'Submitting...' : renderLockedLabel('Submit Corrections', Boolean(correctionsLockReason))}
                  </Button>
                </>
              ) : isApproved ? (
                // APPROVED STATE: Show corrections option only
                <Button
                  onClick={() => setShowCorrectionForm(true)}
                  disabled={isLoading || Boolean(correctionsLockReason)}
                >
                  {renderLockedLabel('Submit Corrections', Boolean(correctionsLockReason))}
                </Button>
              ) : isRejected ? (
                // REJECTED STATE: Approval can reopen/restart review, but rejection is final.
                <>
                  <Button
                    variant="secondary"
                    disabled
                  >
                    {renderLockedLabel('Reject', true)}
                  </Button>
                  <Button
                    disabled={isLoading || Boolean(approveLockReason)}
                    onClick={handleApprove}
                  >
                    {isLoading ? 'Approving...' : renderLockedLabel('Approve', Boolean(approveLockReason))}
                  </Button>
                </>
              ) : (
                // PENDING APPROVAL STATE: Show approval workflow with corrections option
                showRejectForm ? (
                  <>
                    <Button
                      variant="secondary"
                      disabled={isLoading}
                      onClick={() => setShowRejectForm(false)}
                    >
                      Back
                    </Button>
                    <Button
                      variant="danger"
                      disabled={isLoading || !rejectionReason.trim() || Boolean(rejectLockReason)}
                      onClick={handleReject}
                    >
                      {isLoading ? 'Rejecting...' : renderLockedLabel('Confirm Rejection', Boolean(rejectLockReason))}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      disabled={isLoading || Boolean(correctionsLockReason)}
                      onClick={() => setShowCorrectionForm(true)}
                    >
                      {renderLockedLabel('Corrections', Boolean(correctionsLockReason))}
                    </Button>
                    <Button
                      variant="danger"
                      disabled={isLoading || Boolean(rejectLockReason)}
                      onClick={() => setShowRejectForm(true)}
                    >
                      {renderLockedLabel('Reject', Boolean(rejectLockReason))}
                    </Button>
                    <Button
                      disabled={isLoading || Boolean(approveLockReason)}
                      onClick={handleApprove}
                    >
                      {isLoading ? 'Approving...' : renderLockedLabel('Approve', Boolean(approveLockReason))}
                    </Button>
                  </>
                )
              )}
            </div>
          )}
        </div>
        )}

        {/* Mismatch Warning Dialog */}
        {showMismatchDialog && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/40 p-4">
            <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-900">
              <div className="flex items-start gap-3 p-6 border-b border-gray-200 dark:border-gray-700">
                <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Totals don't match
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    The following discrepancies were found. You can go back and fix them or continue anyway.
                  </p>
                </div>
              </div>
              <ul className="min-h-0 flex-1 overflow-y-auto px-6 py-4 space-y-2">
                {mismatchIssues.map((issue, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    {issue}
                  </li>
                ))}
              </ul>
              <div className="flex gap-3 justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowMismatchDialog(false);
                    pendingMismatchAction.current = null;
                  }}
                >
                  Go back and fix
                </Button>
                <Button
                  onClick={handleConfirmMismatchAction}
                  disabled={approveExtraction.isPending || submitCorrections.isPending}
                >
                  {approveExtraction.isPending
                    ? 'Approving...'
                    : submitCorrections.isPending
                      ? 'Submitting...'
                      : pendingMismatchAction.current?.action === 'corrections'
                        ? 'Submit anyway'
                        : 'Approve anyway'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentReviewModal;
